import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

/** Must match --theme-transition-duration in index.css */
const THEME_TRANSITION_MS = 750;

export const THEME_USES_VIEW_TRANSITION =
  typeof document !== 'undefined' && typeof document.startViewTransition === 'function';

function applyThemeClass(root, dark) {
  if (dark) root.classList.add('dark');
  else root.classList.remove('dark');
}

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem('stavyn-theme');
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
    } catch {}
    return true;
  });
  const [isThemeTransitioning, setIsThemeTransitioning] = useState(false);

  useEffect(() => {
    applyThemeClass(document.documentElement, isDark);
  }, [isDark]);

  function toggleTheme() {
    const root = document.documentElement;
    const nextDark = !isDark;

    const commit = () => {
      setIsThemeTransitioning(true);
      if (!THEME_USES_VIEW_TRANSITION) {
        root.classList.add('transitioning');
      }
      applyThemeClass(root, nextDark);
      setIsDark(nextDark);
      try {
        localStorage.setItem('stavyn-theme', nextDark ? 'dark' : 'light');
      } catch {}
    };

    const finish = () => {
      setIsThemeTransitioning(false);
      root.classList.remove('transitioning');
    };

    if (THEME_USES_VIEW_TRANSITION) {
      document.startViewTransition(commit).finished.then(finish).catch(finish);
    } else {
      commit();
      setTimeout(finish, THEME_TRANSITION_MS);
    }
  }

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, isThemeTransitioning }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
